import { useEffect, useMemo } from 'react';
import { Subject } from 'rxjs';

/**
 * @typedef {Object} ModelEvent
 * @property {string} model - The model name/type
 * @property {'create'|'update'|'destroy'} action - The type of event
 * @property {Object.<string, any>} attributes - The model attributes
 */

/**
 * @typedef {Object} useModelEventsReturn
 * @property {Observable<ModelEvent>} create$ - The create event stream
 * @property {Observable<ModelEvent>} update$ - The update event stream
 * @property {Observable<ModelEvent>} destroy$ - The destroy event stream
 * @see https://rxjs.dev/api/index/class/Observable
 */

/**
 * A custom React hook that creates filtered event streams for model events.
 * This hook subscribes to a main event stream and filters events based on model type,
 * action type, and optional attribute filters, providing separate streams for create,
 * update, and destroy events.
 *
 * @param {Subject<ModelEvent>} modelEvents$ - The main event stream to subscribe to. See RxJS Observable.
 * @param {string} model - The model name/type to filter events for
 * @param {Object} [filterAttributes={}] - Optional attributes to further filter events
 *
 * @returns {useModelEventsReturn}
 *
 * @see https://rxjs.dev/api/index/class/Subject
 *
 * @example
 * // Basic usage
 * const { create$, update$, destroy$ } = useModelEvents(modelEvents$, 'User');
 *
 * useEffect(() => {
 *   const subscription = create$.subscribe(event => {
 *     console.log('New user created:', event.attributes);
 *   });
 *   return () => subscription.unsubscribe();
 * }, [create$]);
 *
 * @example
 * // With attribute filtering
 * const { update$ } = useModelEvents(modelEvents$, 'User', { status: 'active' });
 *
 * useEffect(() => {
 *   const subscription = update$.subscribe(event => {
 *     console.log('Active user updated:', event.attributes);
 *   });
 *   return () => subscription.unsubscribe();
 * }, [update$]);
 */
export function useModelEvents(modelEvents$, model, filterAttributes = {}) {
  const create$ = useMemo(() => new Subject(), []);
  const update$ = useMemo(() => new Subject(), []);
  const destroy$ = useMemo(() => new Subject(), []);

  // Subscribe to the main event stream and filter to our specific subjects
  useEffect(() => {
    if (!modelEvents$) return;

    const subscription = modelEvents$.subscribe((event) => {
      if (event.model !== model) return;

      const matchesFilter = Object.entries(filterAttributes).every(
        ([key, value]) => event.attributes[key] === value,
      );

      if (!matchesFilter) return;

      switch (event.action) {
        case 'create':
          create$.next(event);
          break;
        case 'update':
          update$.next(event);
          break;
        case 'destroy':
          destroy$.next(event);
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [modelEvents$, model, filterAttributes, create$, update$, destroy$]);

  return {
    create$,
    update$,
    destroy$,
  };
}
